import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { IncidentsByStatusOutput, IncidentStatusOption } from '@/graphql/generated';
import textsData from '@/textData/DashboardTexts.json';
import { useUserLanguage } from '@/customHooks';
import { theme } from '@/styles';
import { incidentStatusOptionMapper } from '@/helpers';
import { DashboardCardPeriodCaption } from '../atoms';

const INCIDENT_PINK = '#C966B7';
const COLORS = [
  theme.colors.primary1,
  theme.colors.primary2,
  INCIDENT_PINK,
  theme.colors.secondary2,
];

const PENDING_COLOR = 'tomato';

type DashboardIncidentsByStatusCardProps = {
  data: IncidentsByStatusOutput[] | undefined;
};

export const DashboardIncidentsByStatusCard = ({
  data,
}: DashboardIncidentsByStatusCardProps) => {
  const userLanguage = useUserLanguage();
  const texts =
    userLanguage === 'es'
      ? textsData.es.incidentsByStatusCard
      : textsData.en.incidentsByStatusCard;

  const safeData = data ?? [];

  const countsByStatus = safeData.reduce<Record<IncidentStatusOption, number>>((acc, item) => {
    const count = parseInt(item.count ?? '0');
    acc[item.status] = (acc[item.status] || 0) + (Number.isNaN(count) ? 0 : count);
    return acc;
  }, {} as Record<IncidentStatusOption, number>);

  const totalIncidents = Object.values(countsByStatus).reduce((acc, val) => acc + val, 0);

  const newCount = countsByStatus[IncidentStatusOption.New] ?? 0;
  const handledOrPendingCount =
    (countsByStatus[IncidentStatusOption.Completed] ?? 0) +
    (countsByStatus[IncidentStatusOption.Cancelled] ?? 0) +
    (countsByStatus[IncidentStatusOption.Pending] ?? 0) +
    (countsByStatus[IncidentStatusOption.Resolved] ?? 0) +
    (countsByStatus[IncidentStatusOption.Scheduled] ?? 0) +
    (countsByStatus[IncidentStatusOption.Assigned] ?? 0);

  const newPercentage =
    handledOrPendingCount > 0
      ? Math.round((newCount * 100) / handledOrPendingCount)
      : newCount > 0
        ? 100
        : 0;

  if (!totalIncidents) {
    return (
      <div className="min-h-[330px] rounded-xl bg-white px-4 pb-4 pt-3 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
        <div>
          <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
          <DashboardCardPeriodCaption />
          <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />
        </div>
        <p className="m-0 text-gray-600">{texts.empty}</p>
      </div>
    );
  }

  const arrangedData = safeData.map((item) => ({
    name: incidentStatusOptionMapper(item.status, userLanguage),
    value: parseInt(item.count ?? '0'),
    status: item.status,
    color:
      item.status === IncidentStatusOption.Pending
        ? PENDING_COLOR
        : COLORS[safeData.indexOf(item) % COLORS.length],
  }));

  const newLabel = incidentStatusOptionMapper(IncidentStatusOption.New, userLanguage);
  const totalLabel = userLanguage === 'es' ? 'Total' : 'Total';
  const centerSecondaryLabel = userLanguage === 'es'
    ? `${newLabel} (${newPercentage}%)`
    : `${newLabel} (${newPercentage}%)`;

  return (
    <div className="min-h-[330px] rounded-xl bg-white px-4 pb-4 pt-3 shadow-[0_14px_32px_rgba(15,23,42,0.16)]">
      <div>
        <h3 className="m-0 text-[20px] font-bold text-[#1d2f35]">{texts.title}</h3>
        <DashboardCardPeriodCaption />
        <hr className="mb-4 mt-[0.4rem] border-0 border-t border-t-[#E5E7EB]" />
      </div>
      <div className="-mt-0.5 mb-[0.55rem] flex items-center justify-end">
        <span className="text-xs font-semibold text-gray-500">{totalLabel}: {totalIncidents}</span>
      </div>
      <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
        <div className="relative min-h-[200px] w-full">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={arrangedData}
                innerRadius={60}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
              >
                {arrangedData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={arrangedData[index].color}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-[2px]">
            <div className="text-[32px] font-bold leading-none text-gray-900">{newCount}</div>
            <div className="whitespace-nowrap text-center text-xs font-medium leading-[1.1] text-gray-500">{centerSecondaryLabel}</div>
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-[0.65rem]">
          {arrangedData.map((item) => (
            <div key={item.status} className="flex items-center gap-3">
              <div className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
              <p className="m-0 min-w-0 flex-1 text-[15px] leading-[1.2] text-gray-500">{item.name}</p>
              <p className="m-0 min-w-14 shrink-0 text-right text-[15px] font-bold leading-[1.2] text-gray-700">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
